import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import type {
    VectorizeFullResult,
    VectorizeStreamEvent,
} from "~/lib/api/types/imageProcessor";
import {
    addFrame,
    addResult,
    applyStreamEvent,
    createPlaybackState,
    type PlaybackEntry,
    type PlaybackFrame,
} from "~/lib/vectorize/playback";

export type VectorizePlaybackPhase =
    "done" | "error" | "idle" | "upscaling" | "vectorizing";

const REPLAY_FRAME_MS = 1800;
const CLOCK_TICK_MS = 250;

export type VectorizePlayback = ReturnType<typeof useVectorizePlayback>;

/**
 * Zustand des Wiedergabe-Players: sammelt die Ereignisse eines Laufs, hält
 * das gerade gezeigte Bild und kümmert sich um Live-Folgen, Scrubben und
 * automatisches Abspielen nach Abschluss.
 */
export function useVectorizePlayback() {
    const state = reactive(createPlaybackState());
    const phase = ref<VectorizePlaybackPhase>("idle");
    const mode = ref<"edit" | "vectorize">("vectorize");
    const currentIndex = ref<number>(0);
    const following = ref<boolean>(true);
    const playing = ref<boolean>(false);
    const elapsedMs = ref<number>(0);

    let startedAt = 0;
    // Set for a run that already ended: its clock stops at its real end.
    let endedAt: number | undefined;
    let clock: ReturnType<typeof setInterval> | undefined;
    let replay: ReturnType<typeof setInterval> | undefined;

    const frames = computed<PlaybackFrame[]>(() => state.frames);
    const entries = computed<PlaybackEntry[]>(() => state.entries);
    const live = computed(
        () => phase.value === "upscaling" || phase.value === "vectorizing",
    );
    const currentFrame = computed<PlaybackFrame | undefined>(
        () => state.frames[currentIndex.value],
    );
    const lastIndex = computed(() => Math.max(0, state.frames.length - 1));
    const runningTool = computed(() =>
        state.entries.find(
            (entry) => entry.kind === "tool" && entry.status === "running",
        ),
    );

    function stopClock() {
        if (clock) clearInterval(clock);
        clock = undefined;
    }

    function stopReplay() {
        if (replay) clearInterval(replay);
        replay = undefined;
        playing.value = false;
    }

    function now(): number {
        return (endedAt ?? Date.now()) - startedAt;
    }

    function reset(sourceImage: string) {
        stopReplay();
        stopClock();

        state.entries = [];
        state.frames = [];
        state.maxIterations = 0;
        state.step = 0;
        currentIndex.value = 0;
        following.value = true;
        elapsedMs.value = 0;
        phase.value = "idle";
        startedAt = Date.now();
        endedAt = undefined;

        addFrame(state, { at: 0, image: sourceImage, kind: "source" });
    }

    // A queued run started elsewhere: the clock counts from its real start.
    let alignedStart: number | undefined;

    /**
     * Makes the clock show the time since the run really started (and, for a
     * finished one, stop at its end) — for a viewer who joins a run halfway
     * or replays it later.
     */
    function alignClock(start: number | undefined, end?: number) {
        alignedStart = start;
        endedAt = start === undefined ? undefined : end;

        if (start !== undefined) startedAt = start;

        elapsedMs.value = now();
    }

    function follow() {
        if (following.value) currentIndex.value = lastIndex.value;
    }

    function beginRun(kind: "edit" | "vectorize") {
        mode.value = kind;
        startedAt = alignedStart ?? Date.now();
        elapsedMs.value = now();
        stopClock();
        clock = setInterval(() => {
            elapsedMs.value = now();
        }, CLOCK_TICK_MS);
    }

    function startUpscale() {
        beginRun("vectorize");
        phase.value = "upscaling";

        state.entries.push({
            id: "upscale",
            at: 0,
            kind: "upscale",
            status: "running",
        });
    }

    /**
     * Wie {@link startUpscale}, nur ohne den Hochskalier-Schritt: der Lauf
     * bekommt keinen Eintrag dafür, statt einen als „fehlgeschlagen“ zu zeigen.
     */
    function startWithoutUpscale() {
        beginRun("vectorize");
        phase.value = "vectorizing";
    }

    /** Eine Bearbeitung setzt auf einem SVG auf — kein Hochskalieren. */
    function startEdit() {
        beginRun("edit");
        phase.value = "vectorizing";
    }

    function finishUpscale(image: string | undefined) {
        const entry = state.entries.find((item) => item.kind === "upscale");

        if (entry?.kind === "upscale") {
            entry.status = image ? "done" : "failed";
        }

        if (image) {
            addFrame(state, { at: now(), image, kind: "upscaled" });
            follow();
        }
    }

    function startVectorize() {
        phase.value = "vectorizing";
    }

    function push(event: VectorizeStreamEvent) {
        applyStreamEvent(state, event);
        follow();
    }

    function finish(result: VectorizeFullResult) {
        // Das Ergebnis kommt bereits als Stream-Ereignis; nur wenn es nicht
        // kam (z. B. gecachter Lauf ohne Stream) wird es hier ergänzt.
        if (!state.frames.some((frame) => frame.kind === "result")) {
            addResult(state, result, now());
        }

        follow();
        stopClock();
        elapsedMs.value = now();
        phase.value = "done";
    }

    function fail(message: string) {
        stopClock();
        elapsedMs.value = now();

        if (!state.entries.some((entry) => entry.kind === "error")) {
            state.entries.push({
                id: "error",
                at: elapsedMs.value,
                kind: "error",
                message,
            });
        }

        phase.value = "error";
    }

    function seek(index: number) {
        const clamped = Math.max(0, Math.min(index, lastIndex.value));

        currentIndex.value = clamped;
        following.value = clamped === lastIndex.value;

        if (playing.value && clamped === lastIndex.value) stopReplay();
    }

    function next() {
        seek(currentIndex.value + 1);
    }

    function previous() {
        seek(currentIndex.value - 1);
    }

    function backToLive() {
        following.value = true;
        currentIndex.value = lastIndex.value;
    }

    function play() {
        if (state.frames.length < 2) return;

        if (currentIndex.value >= lastIndex.value) seek(0);

        playing.value = true;
        replay = setInterval(() => {
            if (currentIndex.value >= lastIndex.value) {
                stopReplay();

                return;
            }

            seek(currentIndex.value + 1);
        }, REPLAY_FRAME_MS);
    }

    function pause() {
        stopReplay();
    }

    function togglePlay() {
        if (playing.value) {
            pause();
        } else {
            play();
        }
    }

    // Sobald der Lauf beendet ist, wird der Ablauf einmal von vorn abgespielt,
    // falls der Benutzer gerade live mitgeschaut hat.
    watch(phase, (value) => {
        if (value === "done" && following.value) play();
    });

    onBeforeUnmount(() => {
        stopClock();
        stopReplay();
    });

    return {
        alignClock,
        backToLive,
        currentFrame,
        currentIndex,
        elapsedMs,
        entries,
        fail,
        finish,
        finishUpscale,
        following,
        frames,
        lastIndex,
        live,
        maxIterations: computed(() => state.maxIterations),
        mode,
        next,
        pause,
        phase,
        play,
        playing,
        previous,
        push,
        reset,
        runningTool,
        seek,
        startEdit,
        startUpscale,
        startVectorize,
        startWithoutUpscale,
        step: computed(() => state.step),
        togglePlay,
    };
}
